//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { useCurrentUserSettingsQuery } from '@account-settings/accountSettingsQueries';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { SectionBoldText } from '@components/atoms/text/Text';
import makeAngularReactController from '@react-bridge/angular-react-widget';
import { OnboardingResourcesCard } from '@welcome-page/components/onboarding/info/OnboardingResourcesCard';
import { OnboardingSupportCard } from '@welcome-page/components/onboarding/info/OnboardingSupportCard';
import { FeatureSpotlight } from '@welcome-page/components/onboarding/spotlight/FeatureSpotlight';
import { OnboardingCardGrid } from '@welcome-page/components/onboarding/steps/OnboardingStepCardGrid';
import { OnboardingStepStatus } from '@welcome-page/constants/OnboardingConstants';
import { useOnboardingAnalyticsStepInfo } from '@welcome-page/hooks/useOnboardingAnalyticsStepInfo';
import { useOnboardingCatalogStepInfo } from '@welcome-page/hooks/useOnboardingCatalogStepInfo';
import { useOnboardingSocialStepInfo } from '@welcome-page/hooks/useOnboardingSocialStepInfo';

export const OnboardingScreen = () => {
  const {
    data: settings,
    isError: isUserSettingsError,
    isLoading: isLoadingUserSettings,
  } = useCurrentUserSettingsQuery();

  const {
    isError: isSocialStepError,
    isLoading: isLoadingSocial,
    status: socialStatus,
  } = useOnboardingSocialStepInfo();

  const {
    isError: isCatalogStepError,
    isLoading: isLoadingCatalog,
    status: catalogStatus,
  } = useOnboardingCatalogStepInfo();

  const {
    isError: isAnalyticsStepError,
    isLoading: isLoadingAnalytics,
    status: analyticsStatus,
  } = useOnboardingAnalyticsStepInfo();

  const { firstName } = useSelector((state) => state.session.user);

  const shouldShowLoadingState =
    isLoadingAnalytics ||
    isLoadingCatalog ||
    isLoadingSocial ||
    isLoadingUserSettings;

  const areConnectionsComplete = [
    analyticsStatus,
    catalogStatus,
    socialStatus,
  ].every((status) => status === OnboardingStepStatus.COMPLETE);

  if (shouldShowLoadingState) {
    return (
      <SpinnerContainer>
        <CenteredSpinner />
      </SpinnerContainer>
    );
  }

  return (
    <ScreenContainer>
      <ContentContainer>
        <SectionBoldText>
          Welcome to Bazaarvoice Vibe, {firstName}!
        </SectionBoldText>
        {!areConnectionsComplete && (
          <OnboardingCardGrid
            analyticsOnboardingState={{
              isError: isAnalyticsStepError,
              status: analyticsStatus,
            }}
            catalogOnboardingState={{
              isError: isCatalogStepError,
              status: catalogStatus,
            }}
            socialOnboardingState={{
              isError: isSocialStepError,
              status: socialStatus,
            }}
          />
        )}
        <FeatureSpotlight />
        <ResourcesContainer>
          <OnboardingResourcesCard />
          <OnboardingSupportCard />
        </ResourcesContainer>
      </ContentContainer>
    </ScreenContainer>
  );
};

const SpinnerContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const ScreenContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 1330px;
`;

const ContentContainer = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacings[5]};
  padding-top: ${theme.spacings[5]};
  padding-bottom: ${theme.spacings[5]};
  padding-left: 88px;
  padding-right: 88px;
  text-align: left;
`,
);

const ResourcesContainer = styled.div(
  ({ theme }) => `
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  justify-content: space-between;
  grid-gap: ${theme.spacings[5]};
`,
);

angular.module('curalate').component('onboardingScreen', {
  controller: makeAngularReactController(<OnboardingScreen />),
});
